import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { HomeRedirect, RoleFallbackRedirect } from '@/components/auth/home-redirect'
import { FeatureGate } from '@/components/auth/feature-gate'
import { homePathForRole } from '@/lib/auth'

// Layouts
import { AuthLayout } from '@/components/layouts/auth-layout'
import { MemberLayout } from '@/components/layouts/member-layout'
import { AdminLayout } from '@/components/layouts/admin-layout'
import { CommitteeLayout } from '@/components/layouts/committee-layout'
import { OperationsLayout } from '@/components/layouts/operations-layout'

// Auth pages
import { LoginPage } from '@/pages/auth/login'
import { ForgotPasswordPage } from '@/pages/auth/forgot-password'
import { ResetPasswordPage } from '@/pages/auth/reset-password'
import { AcceptInvitePage } from '@/pages/auth/accept-invite'
import { CompleteProfilePage } from '@/pages/auth/complete-profile'
import { QuickPayPage } from '@/pages/pay/quick-pay'
import { PublicReportPage } from '@/pages/public/report'

// Member pages
import { MemberDashboard } from '@/pages/member/dashboard'
import { MemberPlotDetail } from '@/pages/member/plot-detail'
import { MemberBills } from '@/pages/member/bills'
import { MemberInvoiceDetail } from '@/pages/member/invoice-detail'
import { MemberDocuments } from '@/pages/member/documents'
import { MemberRequests } from '@/pages/member/requests'
import { MemberAnnouncements } from '@/pages/member/announcements'
import { MemberAnnouncementDetail } from '@/pages/member/announcement-detail'
import { MemberPolls } from '@/pages/member/polls'
import { MemberPollDetail } from '@/pages/member/poll-detail'
import { MemberResolutionDetail } from '@/pages/member/resolution-detail'
import { MemberProfile } from '@/pages/member/profile'

// Admin pages
import { AdminDashboard } from '@/pages/admin/dashboard'
import { AdminMembers } from '@/pages/admin/members'
import { AdminMembersImport } from '@/pages/admin/members-import'
import { AdminMemberDetail } from '@/pages/admin/member-detail'
import { AdminPlots } from '@/pages/admin/plots'
import { AdminPlotsImport } from '@/pages/admin/plots-import'
import { AdminPlotDetail } from '@/pages/admin/plot-detail'
import { AdminPlotCreate } from '@/pages/admin/plot-create'
import { AdminBilling } from '@/pages/admin/billing'
import { AdminBillingCycleDetail } from '@/pages/admin/billing-cycle-detail'
import { AdminBillingUtilityCycles } from '@/pages/admin/billing-utility-cycles'
import { AdminUtilityTypes } from '@/pages/admin/utility-types'
import { AdminPayments } from '@/pages/admin/payments'
import { AdminDocuments } from '@/pages/admin/documents'
import { AdminAnnouncements } from '@/pages/admin/announcements'
import { AdminAnnouncementDetail } from '@/pages/admin/announcement-detail'
import { AdminRequests } from '@/pages/admin/requests'
import { AdminPolls } from '@/pages/admin/polls'
import { AdminPollDetail } from '@/pages/admin/poll-detail'
import { AdminResolutionDetail } from '@/pages/admin/resolution-detail'
import { AdminApprovals } from '@/pages/admin/approvals'
import { AdminSettings } from '@/pages/admin/settings'
import { AdminAccessControl } from '@/pages/admin/access-control'

// Committee pages
import { CommitteeDashboard } from '@/pages/committee/dashboard'
import { CommitteeBilling } from '@/pages/committee/billing'
import { CommitteeBillingCycleDetail } from '@/pages/committee/billing-cycle-detail'
import { CommitteeBillingUtilityCycles } from '@/pages/committee/billing-utility-cycles'
import { CommitteePayments } from '@/pages/committee/payments'
import { CommitteeApprovals } from '@/pages/committee/approvals'
import { CommitteeMembers } from '@/pages/committee/members'
import { CommitteeMemberDetail } from '@/pages/committee/member-detail'
import { CommitteePlots } from '@/pages/committee/plots'
import { CommitteePlotDetail } from '@/pages/committee/plot-detail'
import { CommitteeAnnouncements } from '@/pages/committee/announcements'
import { CommitteeAnnouncementDetail } from '@/pages/committee/announcement-detail'
import { CommitteePolls } from '@/pages/committee/polls'
import { CommitteePollDetail } from '@/pages/committee/poll-detail'
import { CommitteeResolutionDetail } from '@/pages/committee/resolution-detail'
import { CommitteeRequests } from '@/pages/committee/requests'

// Operations (estate manager) pages
import { OperationsDashboard } from '@/pages/operations/dashboard'
import { OperationsMembers } from '@/pages/operations/members'
import { OperationsMemberDetail } from '@/pages/operations/member-detail'
import { OperationsPlots } from '@/pages/operations/plots'
import { OperationsPlotsImport } from '@/pages/operations/plots-import'
import { OperationsPlotCreate } from '@/pages/operations/plot-create'
import { OperationsPlotDetail } from '@/pages/operations/plot-detail'
import { OperationsBilling } from '@/pages/operations/billing'
import { OperationsBillingCycleDetail } from '@/pages/operations/billing-cycle-detail'
import { OperationsBillingUtilityCycles } from '@/pages/operations/billing-utility-cycles'
import { OperationsPayments } from '@/pages/operations/payments'
import { OperationsAnnouncements } from '@/pages/operations/announcements'
import { OperationsAnnouncementDetail } from '@/pages/operations/announcement-detail'
import { OperationsPolls } from '@/pages/operations/polls'
import { OperationsPollDetail } from '@/pages/operations/poll-detail'
import { OperationsResolutionDetail } from '@/pages/operations/resolution-detail'
import { OperationsRequests } from '@/pages/operations/requests'
import { OperationsApprovals } from '@/pages/operations/approvals'

function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { user, isLoading } = useAuth()
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream font-sans text-sm text-muted-foreground">
        Loading…
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  if (user.requiresOnboarding) return <Navigate to="/complete-profile" replace />
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homePathForRole(user.role)} replace />
  }
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/complete-profile" element={<CompleteProfilePage />} />
          <Route path="/pay/:token" element={<QuickPayPage />} />
          <Route path="/report" element={<PublicReportPage />} />
        </Route>

        {/* Member portal */}
        <Route
          element={
            <ProtectedRoute roles={['member', 'committee', 'estate_manager']}>
              <MemberLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<MemberDashboard />} />
          <Route path="/plots/:plotId" element={<MemberPlotDetail />} />
          <Route path="/bills" element={<MemberBills />} />
          <Route path="/bills/:id" element={<MemberInvoiceDetail />} />
          <Route
            path="/documents"
            element={
              <FeatureGate feature="documents">
                <MemberDocuments />
              </FeatureGate>
            }
          />
          <Route
            path="/requests"
            element={
              <FeatureGate feature="service_requests">
                <MemberRequests />
              </FeatureGate>
            }
          />
          <Route
            path="/announcements"
            element={
              <FeatureGate feature="announcements">
                <MemberAnnouncements />
              </FeatureGate>
            }
          />
          <Route
            path="/announcements/:id"
            element={
              <FeatureGate feature="announcements">
                <MemberAnnouncementDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/polls"
            element={
              <FeatureGate feature="governance">
                <MemberPolls />
              </FeatureGate>
            }
          />
          <Route
            path="/polls/:id"
            element={
              <FeatureGate feature="governance">
                <MemberPollDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/resolutions/:id"
            element={
              <FeatureGate feature="governance">
                <MemberResolutionDetail />
              </FeatureGate>
            }
          />
          <Route path="/profile" element={<MemberProfile />} />
        </Route>

        {/* Operations (estate manager) */}
        <Route
          element={
            <ProtectedRoute roles={['estate_manager']}>
              <OperationsLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/operations" element={<Navigate to="/operations/members" replace />} />
          <Route path="/operations/dashboard" element={<OperationsDashboard />} />
          <Route
            path="/operations/members"
            element={
              <FeatureGate feature="members">
                <OperationsMembers />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/members/:id"
            element={
              <FeatureGate feature="members">
                <OperationsMemberDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/plots"
            element={
              <FeatureGate feature="plots">
                <OperationsPlots />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/plots/import"
            element={
              <FeatureGate feature="plots">
                <OperationsPlotsImport />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/plots/new"
            element={
              <FeatureGate feature="plots">
                <OperationsPlotCreate />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/plots/:id"
            element={
              <FeatureGate feature="plots">
                <OperationsPlotDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/billing"
            element={
              <FeatureGate feature="billing">
                <OperationsBilling />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/billing/cycles/:utilityTypeId"
            element={
              <FeatureGate feature="billing">
                <OperationsBillingUtilityCycles />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/billing/cycles/:utilityTypeId/:billingPeriod"
            element={
              <FeatureGate feature="billing">
                <OperationsBillingCycleDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/payments"
            element={
              <FeatureGate feature="payments">
                <OperationsPayments />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/announcements"
            element={
              <FeatureGate feature="announcements">
                <OperationsAnnouncements />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/announcements/:id"
            element={
              <FeatureGate feature="announcements">
                <OperationsAnnouncementDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/polls"
            element={
              <FeatureGate feature="governance">
                <OperationsPolls />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/polls/:id"
            element={
              <FeatureGate feature="governance">
                <OperationsPollDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/resolutions/:id"
            element={
              <FeatureGate feature="governance">
                <OperationsResolutionDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/requests"
            element={
              <FeatureGate feature="service_requests">
                <OperationsRequests />
              </FeatureGate>
            }
          />
          <Route
            path="/operations/approvals"
            element={
              <FeatureGate feature="approvals">
                <OperationsApprovals />
              </FeatureGate>
            }
          />
        </Route>

        {/* Committee */}
        <Route
          element={
            <ProtectedRoute roles={['committee', 'admin']}>
              <CommitteeLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/committee/dashboard" element={<CommitteeDashboard />} />
          <Route
            path="/committee/billing"
            element={
              <FeatureGate feature="billing">
                <CommitteeBilling />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/billing/cycles/:utilityTypeId"
            element={
              <FeatureGate feature="billing">
                <CommitteeBillingUtilityCycles />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/billing/cycles/:utilityTypeId/:billingPeriod"
            element={
              <FeatureGate feature="billing">
                <CommitteeBillingCycleDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/payments"
            element={
              <FeatureGate feature="payments">
                <CommitteePayments />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/approvals"
            element={
              <FeatureGate feature="approvals">
                <CommitteeApprovals />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/members"
            element={
              <FeatureGate feature="members">
                <CommitteeMembers />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/members/:id"
            element={
              <FeatureGate feature="members">
                <CommitteeMemberDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/plots"
            element={
              <FeatureGate feature="plots">
                <CommitteePlots />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/plots/:id"
            element={
              <FeatureGate feature="plots">
                <CommitteePlotDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/announcements"
            element={
              <FeatureGate feature="announcements">
                <CommitteeAnnouncements />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/announcements/:id"
            element={
              <FeatureGate feature="announcements">
                <CommitteeAnnouncementDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/polls"
            element={
              <FeatureGate feature="governance">
                <CommitteePolls />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/polls/:id"
            element={
              <FeatureGate feature="governance">
                <CommitteePollDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/resolutions/:id"
            element={
              <FeatureGate feature="governance">
                <CommitteeResolutionDetail />
              </FeatureGate>
            }
          />
          <Route
            path="/committee/requests"
            element={
              <FeatureGate feature="service_requests">
                <CommitteeRequests />
              </FeatureGate>
            }
          />
        </Route>

        {/* Admin */}
        <Route
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin" element={<Navigate to="/admin/members" replace />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/members" element={<AdminMembers />} />
          <Route path="/admin/members/import" element={<AdminMembersImport />} />
          <Route path="/admin/members/:id" element={<AdminMemberDetail />} />
          <Route path="/admin/plots" element={<AdminPlots />} />
          <Route path="/admin/plots/import" element={<AdminPlotsImport />} />
          <Route path="/admin/plots/new" element={<AdminPlotCreate />} />
          <Route path="/admin/plots/:id" element={<AdminPlotDetail />} />
          <Route path="/admin/billing" element={<AdminBilling />} />
          <Route
            path="/admin/billing/cycles/:utilityTypeId"
            element={<AdminBillingUtilityCycles />}
          />
          <Route
            path="/admin/billing/cycles/:utilityTypeId/:billingPeriod"
            element={<AdminBillingCycleDetail />}
          />
          <Route path="/admin/utility-types" element={<AdminUtilityTypes />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/documents" element={<AdminDocuments />} />
          <Route path="/admin/announcements" element={<AdminAnnouncements />} />
          <Route path="/admin/announcements/:id" element={<AdminAnnouncementDetail />} />
          <Route path="/admin/requests" element={<AdminRequests />} />
          <Route path="/admin/polls" element={<AdminPolls />} />
          <Route path="/admin/polls/:id" element={<AdminPollDetail />} />
          <Route path="/admin/resolutions/:id" element={<AdminResolutionDetail />} />
          <Route path="/admin/approvals" element={<AdminApprovals />} />
          <Route path="/admin/access-control" element={<AdminAccessControl />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<RoleFallbackRedirect />} />
      </Routes>
    </BrowserRouter>
  )
}
